import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import WholesaleLayout, { useWholesaleAuth } from './WholesaleLayout';
import { getWholesaleProducts, placeWholesaleOrder } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

function StockIndicator({ stock }) {
  if (stock === 0 || stock === undefined) return <div style={{ fontSize:10, letterSpacing:1, fontWeight:700, color:'#C41E2A', textTransform:'uppercase', marginTop:4 }}>OUT OF STOCK</div>;
  if (stock <= 5) return <div style={{ fontSize:10, color:'#E65100', marginTop:4 }}>LOW STOCK — {stock} left</div>;
  return <div style={{ fontSize:10, color:'#888', marginTop:4 }}>{stock} available</div>;
}

export default function WholesaleDashboard() {
  const user = useWholesaleAuth('wholesale');
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (user) getWholesaleProducts().then(r => setProducts(r.data)).catch(() => {}); }, [user]);

  const grouped = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const key = p.model || 'Other';
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [products]);

  const cartCount = Object.values(cart).reduce((s, c) => s + c.qty, 0);
  const cartTotal = Object.values(cart).reduce((s, c) => s + c.price * c.qty, 0);

  const updateQty = (p, delta) => {
    const maxStock = p.stock || 0;
    setCart(prev => {
      const cur = prev[p.id] || { id: p.id, name: `${p.model} - ${p.color}`, price: p.price, qty: 0, hsn: p.hsn || '91021100' };
      const nq = Math.max(0, Math.min(cur.qty + delta, maxStock));
      if (nq === 0) { const n = { ...prev }; delete n[p.id]; return n; }
      return { ...prev, [p.id]: { ...cur, qty: nq } };
    });
  };

  const setQty = (p, val) => {
    const maxStock = p.stock || 0;
    const nq = Math.max(0, Math.min(parseInt(val) || 0, maxStock));
    setCart(prev => {
      if (nq === 0) { const n = { ...prev }; delete n[p.id]; return n; }
      const cur = prev[p.id] || { id: p.id, name: `${p.model} - ${p.color}`, price: p.price, qty: 0, hsn: p.hsn || '91021100' };
      return { ...prev, [p.id]: { ...cur, qty: nq } };
    });
  };

  const removeFromCart = (id) => setCart(prev => { const n = { ...prev }; delete n[id]; return n; });

  const handleSubmit = async () => {
    if (cartCount === 0) return;
    setSubmitting(true);
    try {
      const res = await placeWholesaleOrder({ items: Object.values(cart), notes });
      setCart({}); setNotes(''); setCartOpen(false);
      alert(`Order placed: ${res.data.id}`);
      navigate('/wholesale/orders');
    } catch (e) { alert(e.response?.data?.detail || 'Failed to place order'); }
    setSubmitting(false);
  };

  return (
    <WholesaleLayout user={user} title="Product Catalogue">
      {Object.entries(grouped).map(([model, variants]) => (
        <div key={model} style={{ marginBottom: 40 }}>
          <div style={{ display:'flex', alignItems:'baseline', gap:12, marginBottom:16 }}>
            <h2 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:700, letterSpacing:1 }}>{model}</h2>
            <span style={{ fontSize:12, color:'#888' }}>{variants.length} colorway{variants.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16 }}>
            {variants.map(p => {
              const inCart = !!cart[p.id];
              const oos = !p.stock || p.stock === 0;
              return (
                <div key={p.id} data-testid={`product-card-${p.id}`}
                  onClick={() => { if (!oos && !inCart) updateQty(p, 1); }}
                  style={{ border:`1.5px solid ${inCart ? '#C41E2A' : '#E0DED8'}`, borderRadius:4, overflow:'hidden', cursor: oos ? 'not-allowed' : 'pointer', transition:'border-color 0.15s, box-shadow 0.15s', boxShadow: inCart ? '0 0 0 2px rgba(196,30,42,0.15)' : 'none', background:'#fff', opacity: oos ? 0.5 : 1 }}>
                  <div style={{ height:160, background:'#F7F6F2', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                    {p.image ? <img src={p.image.startsWith('http') ? p.image : `${API}${p.image}`} alt={p.color} style={{ width:'100%', height:'100%', objectFit:'contain' }} /> : <div style={{ color:'#ccc', fontSize:11, letterSpacing:1 }}>NO IMAGE</div>}
                  </div>
                  <div style={{ padding:'12px 14px' }}>
                    <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>{p.color}</div>
                    {p.mrp > 0 && <div style={{ textDecoration:'line-through', color:'#aaa', fontSize:11 }}>${p.mrp.toLocaleString()}</div>}
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:700, color:'#C41E2A' }}>${p.price.toLocaleString()}</div>
                    <div style={{ fontSize:10, color:'#aaa', letterSpacing:1, marginTop:2 }}>WHOLESALE PRICE</div>
                    <StockIndicator stock={p.stock} />
                    {inCart && !oos && (
                      <div onClick={e => e.stopPropagation()} style={{ display:'flex', border:'1px solid #E0DED8', borderRadius:2, marginTop:10, overflow:'hidden' }}>
                        <button onClick={() => updateQty(p, -1)} style={{ width:32, background:'#F7F6F2', border:'none', cursor:'pointer', fontSize:16, fontWeight:600 }}>−</button>
                        <input type="number" value={cart[p.id]?.qty || 0} onChange={e => setQty(p, e.target.value)} max={p.stock || 0} min="0" style={{ flex:1, textAlign:'center', border:'none', outline:'none', fontFamily:'Barlow', fontSize:13, padding:'6px 0' }} />
                        <button onClick={() => updateQty(p, 1)} style={{ width:32, background:'#F7F6F2', border:'none', cursor:'pointer', fontSize:16, fontWeight:600 }}>+</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {products.length === 0 && <div style={{ textAlign:'center', color:'#888', padding:80, fontSize:14 }}>No products available yet.</div>}

      {/* Floating cart button */}
      <div data-testid="cart-fab" onClick={() => setCartOpen(true)} style={{ position:'fixed', right:24, bottom:32, background:'#C41E2A', borderRadius:'50%', width:56, height:56, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', zIndex:160, boxShadow:'0 4px 16px rgba(196,30,42,0.4)', color:'#fff', fontSize:22 }}>
        <span>&#128722;</span>
        {cartCount > 0 && <span style={{ position:'absolute', top:-4, right:-4, background:'#fff', color:'#C41E2A', fontSize:11, fontWeight:700, width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>{cartCount}</span>}
      </div>

      {/* Cart panel */}
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:360, background:'#fff', borderLeft:'1px solid #E0DED8', zIndex:150, transform: cartOpen ? 'translateX(0)' : 'translateX(100%)', transition:'transform 0.25s', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E0DED8', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:700, letterSpacing:1 }}>YOUR ORDER</div>
          <button onClick={() => setCartOpen(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#888' }}>&times;</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 24px' }}>
          {Object.values(cart).length === 0 && <div style={{ color:'#888', fontSize:13, padding:24, textAlign:'center' }}>Cart is empty</div>}
          {Object.values(cart).map(item => {
            const prod = products.find(pr => pr.id === item.id);
            const overStock = prod && item.qty > (prod.stock || 0);
            return (
              <div key={item.id} style={{ padding:'12px 0', borderBottom:'1px solid #F0EEE8', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:500, fontSize:13 }}>{item.name}</div>
                  <div style={{ fontSize:11, color:'#888' }}>{item.qty} &times; ${item.price.toLocaleString()}</div>
                  {overStock && <div style={{ fontSize:10, color:'#C41E2A', fontWeight:600 }}>Exceeds available stock!</div>}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontWeight:600, fontSize:13 }}>${(item.price * item.qty).toLocaleString()}</span>
                  <button onClick={() => removeFromCart(item.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#C41E2A', fontSize:16, fontWeight:700 }}>&times;</button>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding:'16px 24px', borderTop:'1px solid #E0DED8' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12, fontWeight:700, fontSize:15 }}>
            <span>Total</span><span>${cartTotal.toLocaleString()}</span>
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" style={{ width:'100%', padding:'10px 12px', border:'1px solid #E0DED8', borderRadius:2, fontFamily:'Barlow', fontSize:13, marginBottom:12, resize:'vertical', minHeight:60 }} />
          <button data-testid="submit-order-btn" onClick={handleSubmit} disabled={submitting || cartCount === 0}
            style={{ width:'100%', padding:'12px 16px', background: cartCount > 0 ? '#C41E2A' : '#ccc', color:'#fff', border:'none', borderRadius:2, fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', cursor: cartCount > 0 ? 'pointer' : 'not-allowed' }}>
            {submitting ? 'Placing Order...' : 'Submit Order'}
          </button>
        </div>
      </div>
      {cartOpen && <div onClick={() => setCartOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:140 }} />}
    </WholesaleLayout>
  );
}
